'use client';

import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import type { NavigationItemUI } from '@/logic/page-config/navigation';
import { useSnowplowUm } from '@/tracking/snowplow';
import type { EventType } from '@/tracking/snowplow/utils';
import {
    EVENT_TYPE,
    getNavigationMenuElementData,
    SNOWPLOW_CONTEXT_MAPPING,
    TOP_NAV_SNOWPLOW_BANNER_PROPS
} from '@/tracking/snowplow/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import {
    getDraggableClickableHorizontalBounds,
    isRectIntersectingClickableHorizontalBandWithBounds
} from '@/utils/tracking/draggable-clickable-bounds';

type TopNavImpressionTrackerProps = {
    items: NavigationItemUI[];
    children: ReactNode;
};

const DRAGGABLE_WRAPPER_SELECTOR = '.draggable-wrapper';

const SCROLL_DEBOUNCE_MS = 150;

const getVisibleNavIndices = (scrollable: HTMLElement | null): Set<number> => {
    if (!scrollable) return new Set();

    const bounds = getDraggableClickableHorizontalBounds(scrollable);
    return new Set(
        Array.from(scrollable.children).flatMap((child, i) =>
            isRectIntersectingClickableHorizontalBandWithBounds(child.getBoundingClientRect(), bounds) ? [i] : []
        )
    );
};

export const TopNavImpressionTracker = ({ items, children }: TopNavImpressionTrackerProps) => {
    const pathname = usePathname();
    const containerRef = useRef<HTMLDivElement>(null);
    const previousPathnameRef = useRef<string | null>(null);
    const { sendEventToSnowplow } = useSnowplowUm();

    const fireTopNavEvent = useCallback(
        async (eventType: EventType, bannerAction: string) => {
            if (items.length === 0) return;

            await ensureCoreEventsInitialized();

            const scrollable = containerRef.current?.querySelector(DRAGGABLE_WRAPPER_SELECTOR) as HTMLElement | null;
            const visibleIndices = getVisibleNavIndices(scrollable);
            const contexts = items.map((item, index) => ({
                contextType: SNOWPLOW_CONTEXT_MAPPING.NAVIGATION_MENU_ELEMENT,
                contextProps: getNavigationMenuElementData(
                    item.name,
                    index + 1,
                    'top_bar',
                    'top_nav',
                    visibleIndices.has(index),
                    item.pinned,
                    item.isPersonalized
                )
            }));

            sendEventToSnowplow({
                event: eventType,
                eventProps: { ...TOP_NAV_SNOWPLOW_BANNER_PROPS, bannerAction },
                contexts
            });
        },
        [items, sendEventToSnowplow]
    );

    const debouncedScrollHandler = useDebounce(() => {
        (async () => {
            try {
                await fireTopNavEvent(EVENT_TYPE.TOP_NAV_SCROLL, 'scroll');
            } catch {
                // Logged in ensureCoreEventsInitialized.
            }
        })();
    }, SCROLL_DEBOUNCE_MS);

    useEffect(() => {
        if (items.length === 0) return;

        const path = (pathname ?? '').split('?')[0];
        const prevPath = previousPathnameRef.current;
        previousPathnameRef.current = path;

        if (prevPath === path) return;

        (async () => {
            try {
                await fireTopNavEvent(EVENT_TYPE.TOP_NAV_IMPRESSION, 'initial_load');
            } catch {
                // Logged in ensureCoreEventsInitialized.
            }
        })();
    }, [pathname, items, fireTopNavEvent]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return () => {};

        const scrollable = container.querySelector(DRAGGABLE_WRAPPER_SELECTOR);
        if (!scrollable) return () => {};

        scrollable.addEventListener('scroll', debouncedScrollHandler, { passive: true });

        return () => {
            scrollable.removeEventListener('scroll', debouncedScrollHandler);
        };
    }, [debouncedScrollHandler]);

    return <div ref={containerRef}>{children}</div>;
};
